import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Brand from "../components/Brand";
import Icon from "../components/Icon";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";
import "./LandingPage.css";

const destinations = [
  {
    city: "Lisbon",
    country: "Portugal",
    category: "City energy",
    image: "lisbon",
    tag: "A different kind of office",
    detail: "Hillside cafés. Atlantic weekends.",
    timezone: "WET / WEST",
  },
  {
    city: "Bali",
    country: "Indonesia",
    category: "Slow living",
    image: "bali",
    tag: "Find your own rhythm",
    detail: "Morning surf. A little more headspace.",
    timezone: "UTC +8",
  },
  {
    city: "Mexico City",
    country: "Mexico",
    category: "City energy",
    image: "mexico",
    tag: "Stay curious, stay a while",
    detail: "Creative neighbourhoods. Endless discovery.",
    timezone: "UTC −6",
  },
];

export default function LandingPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [filter, setFilter] = useState("All places");
  const [menuOpen, setMenuOpen] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  const startPlanning = (idea = prompt) => {
    if (idea.trim()) sessionStorage.setItem("tripPrompt", idea.trim());
    navigate(isAuthenticated ? "/chat" : "/signup");
  };

  return (
    <div className="landing">
      <a className="skip-link" href="#main">
        {t("Skip to content")}
      </a>
      <header className="site-header">
        <div className="site-nav page-width">
          <Brand />
          <nav
            className={menuOpen ? "nav-links is-open" : "nav-links"}
            aria-label={t("Main navigation")}
          >
            <a href="#destinations" onClick={() => setMenuOpen(false)}>
              {t("The destinations")}
            </a>
            <a href="#how-it-works" onClick={() => setMenuOpen(false)}>
              {t("How it works")}
            </a>
            <Link to="/trip-preview">
              {t("The travel brief")}{" "}
              <span className="new-label">{t("PREVIEW")}</span>
            </Link>
            <Link
              className="mobile-account-link"
              to={isAuthenticated ? "/chat" : "/login"}
            >
              {t(isAuthenticated ? "My trips" : "Log in")}
            </Link>
            <button
              className="mobile-account-link"
              onClick={() => startPlanning()}
            >
              {t("Plan a trip")} <Icon name="diagonal" size={16} />
            </button>
          </nav>
          <div className="nav-account">
            <LanguageSwitcher />
            <Link
              className="sign-in-link"
              to={isAuthenticated ? "/chat" : "/login"}
            >
              {t(isAuthenticated ? "My trips" : "Log in")}
            </Link>
            <button
              className="button button-lime button-small"
              onClick={() => startPlanning()}
            >
              {t("Plan a trip")} <Icon name="diagonal" size={16} />
            </button>
            <button
              className="menu-button"
              aria-label={t(menuOpen ? "Close menu" : "Open menu")}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <Icon name={menuOpen ? "close" : "menu"} />
            </button>
          </div>
        </div>
      </header>
      <main id="main">
        <section className="hero-section page-width">
          <div className="hero-copy">
            <div className="eyebrow">
              <span className="live-dot" /> {t("LESS LOGISTICS. MORE LIVING.")}
            </div>
            <h1>
              {t("Your life.")}
              <br />
              {t("A little more")}
              <br />
              <span>{t("out there.")}</span>
              <span className="heading-star" aria-hidden="true">
                <Icon name="asterisk" />
              </span>
            </h1>
            <p className="hero-intro">
              {t("A new city. A better routine. A flight out.")}
              <br />
              {t("Build your next stay around your work and your life.")}
              <br className="desktop-break" />{" "}
              {t("Bring your ideas. We’ll help you see how they fit.")}
            </p>
            <div className="hero-actions">
              <button
                className="button button-lime"
                onClick={() => {
                  promptRef.current?.focus();
                  promptRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }}
              >
                {t("Find my next chapter")} <Icon name="arrow" />
              </button>
              <Link className="text-link" to="/trip-preview">
                {t("See a travel brief")} <Icon name="diagonal" size={16} />
              </Link>
            </div>
            <div className="hero-footnote">
              <Icon name="globe" size={15} />{" "}
              {t("Built for people who can work from anywhere.")}
            </div>
          </div>
          <div className="hero-art">
            <div className="photo-coordinates">
              <span>38°43′ N 9°08′ W</span>
              <span>{t("YOUR NEXT CHAPTER ↗")}</span>
            </div>
            <div className="hero-photo">
              <img
                src="/images/lisbon-960.webp"
                srcSet="/images/lisbon-480.webp 480w, /images/lisbon-960.webp 960w"
                sizes="(max-width: 600px) calc(100vw - 40px), (max-width: 850px) 45vw, 580px"
                alt={t("Lisbon’s terracotta rooftops below São Jorge Castle")}
                fetchPriority="high"
              />
              <div className="photo-grain" />
              <div className="photo-topline">
                <span>
                  <span className="live-dot" /> {t("OPEN TO POSSIBILITY")}
                </span>
                <Icon name="globe" size={23} />
              </div>
              <div className="photo-caption">
                <div>
                  <span>{t("THE WORLD IS YOUR WORKSPACE")}</span>
                  <h2>{t("Olá, Lisbon.")}</h2>
                </div>
                <Link
                  to="/trip-preview"
                  className="photo-link"
                  aria-label={t("Explore the Lisbon sample travel brief")}
                >
                  <Icon name="diagonal" size={26} />
                </Link>
              </div>
            </div>
            <div className="trip-floating-card">
              <div className="floating-icon">
                <Icon name="plane" size={21} />
              </div>
              <div>
                <span className="tiny-label">{t("NEXT STOP")}</span>
                <strong>{t("A change of scenery.")}</strong>
                <span>{t("Your workdays, bases + weekends")}</span>
              </div>
              <span className="floating-check">
                <Icon name="check" size={16} />
              </span>
            </div>
            <div className="photo-bottomline">
              <span>{t("GO SOMEWHERE THAT FEELS LIKE YOU.")}</span>
              <span>01 / 03</span>
            </div>
          </div>
        </section>
        <section
          className="planner-section page-width"
          aria-labelledby="planner-heading"
        >
          <div className="planner-heading">
            <span className="section-index">{t("01 / THE POSSIBILITIES")}</span>
            <h2 id="planner-heading">{t("Where are we taking you?")}</h2>
            <p>{t("A rough idea is a good place to start.")}</p>
          </div>
          <form
            className="trip-composer"
            onSubmit={(event) => {
              event.preventDefault();
              startPlanning();
            }}
          >
            <div className="composer-field">
              <Icon name="plane" size={24} />
              <label className="sr-only" htmlFor="trip-idea">
                {t("Tell us about your trip")}
              </label>
              <textarea
                id="trip-idea"
                ref={promptRef}
                rows={2}
                maxLength={2000}
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder={t(
                  "A month in Lisbon, working London hours. Quiet calls, no car, and weekends by the coast. Help me think it through…",
                )}
              />
            </div>
            <div className="composer-bottom">
              <span>
                <span className="live-dot" />{" "}
                {t("Your next chapter starts with a conversation")}
              </span>
              <button type="submit" className="button button-lime">
                {t("Let’s plan it")} <Icon name="arrow" size={18} />
              </button>
            </div>
          </form>
          <div className="prompt-suggestions">
            <span>{t("Or start here")}</span>
            {[
              "A month in Lisbon",
              "A slower week in Bali",
              "Mexico City, here I come",
            ].map((idea) => (
              <button
                key={idea}
                onClick={() => {
                  setPrompt(t(idea));
                  promptRef.current?.focus();
                }}
              >
                {t(idea)}
                <Icon name="diagonal" size={13} />
              </button>
            ))}
          </div>
        </section>
        <section
          className="destinations-section page-width"
          id="destinations"
          aria-labelledby="destinations-heading"
        >
          <div className="section-heading">
            <div>
              <span className="section-index">{t("02 / SOMEWHERE GOOD")}</span>
              <h2 id="destinations-heading">{t("New timezone. Same you.")}</h2>
              <p>
                {t(
                  "For the places you’ve saved. And the ones you haven’t found yet.",
                )}
              </p>
            </div>
            <div className="filter-group" aria-label={t("Filter destinations")}>
              {["All places", "City energy", "Slow living"].map((item) => (
                <button
                  key={item}
                  aria-pressed={filter === item}
                  onClick={() => setFilter(item)}
                >
                  {t(item)}
                </button>
              ))}
            </div>
          </div>
          <div className="destination-grid">
            {destinations
              .filter(
                (destination) =>
                  filter === "All places" || destination.category === filter,
              )
              .map((destination) => (
                <button
                  className="destination-card"
                  key={destination.city}
                  onClick={() =>
                    startPlanning(
                      t("I'd like to plan a trip to {destination}.", {
                        destination: t(destination.city),
                      }),
                    )
                  }
                >
                  <div className="destination-photo">
                    <img
                      src={`/images/${destination.image}-${destination.image === "lisbon" ? 960 : 800}.webp`}
                      srcSet={`/images/${destination.image}-480.webp 480w, /images/${destination.image}-${destination.image === "lisbon" ? 960 : 800}.webp ${destination.image === "lisbon" ? 960 : 800}w`}
                      sizes="(max-width: 600px) calc(100vw - 40px), (max-width: 850px) 30vw, 380px"
                      alt={`${t(destination.city)}, ${t(destination.country)}`}
                      loading="lazy"
                    />
                    <span className="destination-tag">
                      {t(destination.tag)}
                    </span>
                    <span className="destination-arrow">
                      <Icon name="diagonal" />
                    </span>
                    <div className="destination-name">
                      <span>{t(destination.country)}</span>
                      <h3>{t(destination.city)}</h3>
                    </div>
                  </div>
                  <div className="destination-meta">
                    <span>{t(destination.detail)}</span>
                    <span>{destination.timezone}</span>
                  </div>
                </button>
              ))}
          </div>
          <div className="destination-note">
            <Icon name="pin" size={15} />
            <span>
              {t("A little inspiration. Your trip can start anywhere.")}
            </span>
            <span>{t("YOU PICK THE PIN. WE HELP WITH THE PLAN.")}</span>
          </div>
        </section>
        <section className="how-section" id="how-it-works">
          <div className="page-width">
            <div className="section-heading">
              <div>
                <span className="section-index">
                  {t("03 / FROM IDEA TO ITINERARY")}
                </span>
                <h2>
                  {t("Less tab hopping.")}
                  <br />
                  {t("More actual going.")}
                </h2>
              </div>
              <p className="section-aside">
                {t(
                  "One conversation to bring your travel plans into focus. You make the final call.",
                )}
              </p>
            </div>
            <div className="steps-grid">
              {[
                {
                  icon: "globe" as const,
                  title: "Start with your kind of life.",
                  text: "Your working hours, a place you’re curious about, or a booking you already have. You don’t need all the answers to start.",
                },
                {
                  icon: "plane" as const,
                  title: "Build a stay that fits.",
                  text: "Bring bases, workspaces, experiences, and practical details together. Check local work hours, missing nights, and the cost of changing your mind.",
                },
                {
                  icon: "document" as const,
                  title: "Keep the whole picture.",
                  text: "Your own saved travel brief, built from your plan. Keep suggestions separate from confirmed bookings, and see what still needs checking.",
                },
              ].map((step, index) => (
                <article className="step" key={step.title}>
                  <div className="step-top">
                    <Icon name={step.icon} size={25} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3>{t(step.title)}</h3>
                  <p>{t(step.text)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section className="brief-promo page-width">
          <div className="brief-mini" aria-hidden="true">
            <div className="mini-brand">
              sayitsorted.<span>{t("TRAVEL BRIEF / 001")}</span>
            </div>
            <div className="mini-title">
              {t("A little Lisbon.")}
              <br />
              {t("A lot to look forward to.")}
            </div>
            <div className="mini-rule" />
            <div className="mini-route">
              <span>LHR</span>
              <Icon name="plane" />
              <span>LIS</span>
            </div>
            <div className="mini-bottom">
              <span>{t("YOUR WORK. YOUR STAY. YOUR OWN RHYTHM.")}</span>
              <Icon name="check" size={16} />
            </div>
          </div>
          <div className="brief-promo-copy">
            <span className="section-index">
              {t("EVERY DETAIL, IN ITS PLACE")}
            </span>
            <h2>
              {t("A plan you’ll")}
              <br />
              {t("actually want to open.")}
            </h2>
            <p>
              {t(
                "Your bases, your working week, your next steps. A saved brief that grows with the plan, with clear costs and honest unknowns.",
              )}
            </p>
            <Link className="button button-outline" to="/trip-preview">
              {t("Explore a sample brief")} <Icon name="diagonal" size={18} />
            </Link>
          </div>
        </section>
        <section className="closing-section page-width">
          <span className="eyebrow">
            {t("THERE’S A WHOLE WORLD OUT THERE.")}
          </span>
          <h2>
            {t("Make yourself")}
            <br />
            {t("at home in it.")}
          </h2>
          <button
            className="button button-lime"
            onClick={() => startPlanning()}
          >
            {t("Let’s find your next chapter")} <Icon name="arrow" />
          </button>
          <span className="closing-asterisk" aria-hidden="true">
            <Icon name="asterisk" />
          </span>
        </section>
      </main>
      <footer className="site-footer page-width">
        <Brand />
        <span>{t("A little less planning. A little more living.")}</span>
        <span>© {new Date().getFullYear()} Say it sorted</span>
        <a href="#main" className="back-to-top">
          {t("Back to top ↑")}
        </a>
      </footer>
    </div>
  );
}
